import React from 'react'
import { plural } from '../utils/plural'

/** The soak period is a POLICY number — it varies per customer and per app tier, and 10 days or 21
 *  days are ordinary answers — so the control is a free number of days and these are shortcuts to
 *  the usual ones, not the vocabulary. */
export const SOAK_PRESETS = [0, 3, 7, 14, 30]

/** Days. Past a quarter, "wait" is really a decision not to follow the source at all, which the
 *  Follow new versions switch already says. The API's own clamp is looser (0-365); this is the
 *  narrower bound the control offers, so every value it can produce is accepted. */
export const SOAK_MAX = 90

/** The mock's own option set (its DeploymentTemplateWizard.tsx:830-836). A list rather than a
 *  number because these are whole app objects kept in the tenant: past a handful the answer is
 *  clutter, not a policy anyone tunes. */
export const KEEP_OPTIONS = [
  { value: '0', label: 'Only the latest — remove the previous version' },
  { value: '1', label: 'Keep 1 previous version' },
  { value: '2', label: 'Keep 2 previous versions' },
  { value: '3', label: 'Keep 3 previous versions (recommended)' },
  { value: '5', label: 'Keep 5 previous versions' },
]

/** KEEP_OPTIONS with `current` added when it is not among them. The API accepts 0 to 10, so a
 *  policy can hold a count the list does not offer, and a select whose value matches none of its
 *  options silently shows the first one instead. */
export function keepOptionsIncluding(current: number): { value: string; label: string }[] {
  if (!Number.isInteger(current) || KEEP_OPTIONS.some((o) => Number(o.value) === current)) {
    return KEEP_OPTIONS
  }
  return [...KEEP_OPTIONS, { value: String(current), label: `Keep ${plural(current, 'previous version')}` }].sort(
    (a, b) => Number(a.value) - Number(b.value),
  )
}

/** The number of days `text` names, or null while it is not a whole number from 0 to SOAK_MAX. */
export function soakDaysValue(text: string): number | null {
  const t = text.trim()
  if (!t) return null
  const n = Number(t)
  return Number.isInteger(n) && n >= 0 && n <= SOAK_MAX ? n : null
}

/** Why `text` is not a number of days to wait, or null when it is one. */
export function soakDaysError(text: string): string | null {
  const t = text.trim()
  if (!t) return 'Enter a number of days, or 0 to publish as soon as the version is packaged.'
  const n = Number(t)
  if (!Number.isInteger(n)) return 'Whole days only.'
  if (n < 0 || n > SOAK_MAX) return `Between 0 and ${SOAK_MAX} days.`
  return null
}

/** The wait-before-publishing control: a free number of days, with the usual answers as shortcuts.
 *
 *  The parent owns the raw text, because a number input is empty for a keystroke whenever it is
 *  retyped and the committed number has no value that means "mid-edit". The parent commits
 *  `soakDaysValue(text)` when that is a number and shows `soakDaysError(text)` otherwise, so a
 *  typed value is never silently rounded into something the admin did not choose. */
export default function SoakDaysInput({
  text,
  onChange,
  inputClassName = '',
}: {
  text: string
  onChange: (text: string) => void
  inputClassName?: string
}) {
  const selected = soakDaysValue(text)
  return (
    <>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={0}
          max={SOAK_MAX}
          step={1}
          aria-label="Days to wait after release before publishing"
          className={`${inputClassName} w-24`}
          value={text}
          onChange={(e) => onChange(e.target.value)}
        />
        <span className="text-sm text-theme-text-secondary">days after release</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {SOAK_PRESETS.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(String(n))}
            className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
              selected === n
                ? 'border-blue-500 bg-blue-500/15 text-blue-400'
                : 'border-theme-border-primary bg-theme-secondary text-theme-text-tertiary hover:text-theme-text-primary'
            }`}
          >
            {n === 0 ? 'Immediately' : plural(n, 'day')}
          </button>
        ))}
      </div>
    </>
  )
}
